import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Modal, Spinner } from 'react-bootstrap';
import { useGetMyPreferencesQuery, useSetMyTimezoneMutation } from '../app/api';

const brand = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  charcoal:  '#58585b',
  softGray:  '#e9e6e1',
  green:     '#1a7f4e',
};

/** Personal settings, opened by clicking your own name in the sidebar footer.
 *
 * ALSO WHERE YOU SIGN OUT. The footer used to carry Logout as its own row beside
 * the name — two lines for one subject, and the most destructive action in the
 * menu sitting permanently one stray click from whatever you were reading. It
 * lives in here now: one extra click, and no longer an accident.
 *
 * The time-zone choice lives on the server, not in the browser: every calculation
 * it drives (the "since 5:00 PM the previous business day" cutoff, the day grid)
 * happens server-side, so a localStorage value would be invisible where it's
 * actually needed — and would not follow the user to another device.
 */
export default function UserSettingsModal({ show, onHide, userEmail, onLogout }) {
  // Only fetch once the dialog is actually opened; this sits in the sidebar on
  // every page and shouldn't add a request to every page load.
  const { data, isLoading, isError, refetch } = useGetMyPreferencesQuery(undefined, { skip: !show });
  const [saveTimezone, { isLoading: saving }] = useSetMyTimezoneMutation();

  const [choice, setChoice] = useState('');
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (data) setChoice(data.timezone || data.default_timezone || '');
  }, [data]);

  // Reset the transient banners each time it's reopened, so a previous "Saved"
  // doesn't greet the next visit.
  useEffect(() => {
    if (show) { setSaved(false); setError(null); }
  }, [show]);

  const choices = data?.timezone_choices || [];
  const current = data?.timezone || '';
  const dirty = choice !== current && !(choice === data?.default_timezone && !current);

  const onSave = async () => {
    setError(null); setSaved(false);
    try {
      await saveTimezone({ timezone: choice }).unwrap();
      setSaved(true);
      refetch();
    } catch (e) {
      setError(e?.data?.detail || 'Could not save your time zone. Please try again.');
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton style={{ borderBottom: `1px solid ${brand.softGray}` }}>
        <Modal.Title style={{ color: brand.deepBlue, fontWeight: 800, fontSize: '1.1rem' }}>
          <i className="bi bi-gear-fill me-2" style={{ color: brand.goldenrod }} />
          Settings
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {userEmail && (
          <p style={{ color: brand.charcoal, fontSize: '0.82rem', marginBottom: '1rem' }}>
            Signed in as <strong style={{ color: brand.deepBlue }}>{userEmail}</strong>
          </p>
        )}

        {isLoading ? (
          <div className="text-center py-3"><Spinner animation="border" size="sm" style={{ color: brand.tealBlue }} /></div>
        ) : isError ? (
          <Alert variant="danger" className="py-2 mb-0" style={{ fontSize: '0.85rem' }}>
            Could not load your settings.
          </Alert>
        ) : (
          <>
            <label htmlFor="pref-timezone" style={{ display: 'block' }}>
              <span style={{ fontWeight: 700, color: brand.deepBlue, fontSize: '0.92rem' }}>Time zone</span>
              <div style={{ color: brand.charcoal, fontSize: '0.8rem', margin: '0.15rem 0 0.5rem' }}>
                Sets when your day starts and ends on the Your Day page — the morning
                briefing window and the times on your schedule. Defaults to Eastern.
              </div>
            </label>
            <select
              id="pref-timezone"
              value={choice}
              onChange={(e) => { setChoice(e.target.value); setSaved(false); }}
              disabled={saving}
              style={{
                width: '100%', border: `1px solid #cdd5dd`, borderRadius: 8,
                padding: '0.45rem 0.6rem', fontSize: '0.9rem', color: brand.deepBlue,
                background: 'white', cursor: 'pointer',
              }}
            >
              {choices.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
              {/* A zone set previously (or via the API) that isn't in the curated
                  list must still be selectable, or opening this dialog would
                  silently reset it to Eastern on save. */}
              {current && !choices.some((c) => c.value === current) && (
                <option value={current}>{current}</option>
              )}
            </select>

            {!current && (
              <div style={{ color: brand.charcoal, fontSize: '0.78rem', marginTop: '0.5rem' }}>
                You haven’t set one yet, so {data?.default_timezone} is being used.
              </div>
            )}

            {saved && (
              <Alert variant="success" className="py-2 mt-3 mb-0" style={{ fontSize: '0.82rem' }}>
                <i className="bi bi-check-circle-fill me-1" />
                Saved. Your Day will rebuild in this time zone.
              </Alert>
            )}
            {error && (
              <Alert variant="danger" className="py-2 mt-3 mb-0" style={{ fontSize: '0.82rem' }}>
                {error}
              </Alert>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer style={{ borderTop: `1px solid ${brand.softGray}` }}>
        {/* Hard left, with the confirm/save pair over on the right. Sitting
            beside Save is how someone means to keep a time-zone change and ends
            up back at the login screen. */}
        {onLogout && (
          <button
            type="button"
            onClick={onLogout}
            style={{
              marginRight: 'auto',
              border: `1px solid ${brand.softGray}`, background: 'white',
              color: brand.charcoal, borderRadius: 8, padding: '0.45rem 0.9rem',
              fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer',
            }}
          >
            <i className="bi bi-box-arrow-right me-1" />
            Sign out
          </button>
        )}
        <button
          type="button"
          onClick={onHide}
          style={{
            border: `1px solid #cdd5dd`, background: 'white', color: brand.deepBlue,
            borderRadius: 8, padding: '0.45rem 0.9rem', fontSize: '0.85rem', fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          Close
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={saving || isLoading || isError || !dirty}
          style={{
            border: 'none',
            background: (saving || !dirty) ? brand.softGray : brand.deepBlue,
            color: (saving || !dirty) ? brand.charcoal : 'white',
            borderRadius: 8, padding: '0.45rem 0.9rem', fontSize: '0.85rem', fontWeight: 700,
            cursor: (saving || !dirty) ? 'default' : 'pointer',
          }}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </Modal.Footer>
    </Modal>
  );
}

UserSettingsModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  userEmail: PropTypes.string,
  // Optional: without it the dialog is settings-only and shows no Sign out.
  onLogout: PropTypes.func,
};

UserSettingsModal.defaultProps = {
  userEmail: '',
  onLogout: null,
};
